import axios from 'axios';
import { DynamicProposal } from 'ts/shared/interfaces/CreateProposalParams';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { resolveChildrenOccupancy } from 'utils/hotel';

export const loadGoogleByProposal = async (htl: any, lang: string, token: string) => {
  const childrenOccupancy = resolveChildrenOccupancy(htl.children, htl.childrenAges);
  const response = await searchSerpHotels({
    recaptchaToken: token,
    cleanHotelName: htl.item.item.name,
    bpid: htl.item.item.bpid,
    countryCode: htl.item.addressInfo.country,
    lat: htl.item.position.latitude,
    lon: htl.item.position.longitude,
    cleanAddress: htl.item.addressInfo.address,
    query: {
      q: `Hotel: ${htl.item.item.name}. Address: ${htl.item.addressInfo.address}, ${htl.item.addressInfo.city}, ${htl.item.addressInfo.country}`,
      gL: 'il',
      hl: lang,
      checkIn: htl.checkIn || '',
      checkOut: htl.checkOut || '',
      adults: htl.adults || 0,
      ...childrenOccupancy,
      currency: htl.currency?.toUpperCase() || 'USD',
    },
  });

  return response;
};

export const loadGoogleByDynamicProposal = async (
  dyn: DynamicProposal,
  lang: string,
  token: string,
) => {
  if (!dyn.hotel || !dyn.flight) return;

  const hotelRoom = dyn.hotel?.roomClasses?.[0]?.hotelRooms?.[0];
  const childrenOccupancy = resolveChildrenOccupancy(
    hotelRoom?.numCnn,
    hotelRoom?.pax?.childrenAges,
  );
  const response = await searchSerpHotels({
    recaptchaToken: token,
    cleanHotelName: dyn.hotel.item.name,
    bpid: dyn.hotel.item.bpid,
    countryCode: dyn.hotel.addressInfo.country,
    lat: dyn.hotel.position.latitude,
    lon: dyn.hotel.position.longitude,
    cleanAddress: dyn.hotel.addressInfo.address,
    query: {
      q: `Hotel: ${dyn.hotel.name}. Address: ${dyn.hotel.addressInfo.address}, ${dyn.hotel.addressInfo.city}, ${dyn.hotel.addressInfo.country}`,
      gL: 'il',
      hl: lang,
      checkIn: dyn.hotel?.roomClasses?.[0]?.checkIn || '',
      checkOut: dyn.hotel?.roomClasses?.[0]?.checkOut || '',
      adults: hotelRoom?.numAdt || 0,
      ...childrenOccupancy,
      currency: dyn.currency?.toUpperCase() || 'USD',
    },
  });

  return response;
};
export const searchSerpHotels = async (params: any): Promise<any | undefined> => {
  try {
    return (await axios.post('/Hotels/SearchSerpHotels', params)).data;
  } catch (err) {
    return undefined;
  }
};

export const hotelsTripadvisor = async (params: any): Promise<any | undefined> => {
  try {
    return (await axios.post('/Hotels/TripAdvisor', params)).data;
  } catch (err) {
    return undefined;
  }
};

export const getProposalPreview = async (
  previewKeyPublic: string,
): Promise<ProposalData | undefined> => {
  try {
    const url = 'Utils/GetProposalPreviewPublic';

    return (await axios.post(url, { previewKeyPublic: previewKeyPublic?.toUpperCase() })).data
      .proposal;
  } catch (err) {
    return undefined;
  }
};

export const searchProposals = async (): Promise<ProposalData[] | undefined> => {
  try {
    const url = 'Utils/SearchProposals';
    const userStr = localStorage.getItem('user');
    const userJson = userStr ? JSON.parse(userStr) : '';

    return (await axios.post(url, { userCode: userJson.userCode })).data.proposals;
  } catch (err) {
    return undefined;
  }
};
interface GetProposalPdfByUrlParams {
  previewKeyPublic: string;
  recaptchaToken: string;
  printHtml: string;
  header: string;
  footer: string;
}
export const getProposalPdfByUrl = async (params: GetProposalPdfByUrlParams): Promise<any> => {
  try {
    const url = 'Utils/GetProposalPdfByUrlPublic';
    return (
      await axios.post(url, { ...params, previewKeyPublic: params.previewKeyPublic?.toUpperCase() })
    ).data;
  } catch (err) {
    return undefined;
  }
};

export const checkSearchCache = async (keys: any): Promise<any | undefined> => {
  try {
    const url = 'Utils/CheckSearchCache';
    let result = await axios.post(url, { keys });
    return result.data.response;
  } catch (err) {
    return undefined;
  }
};

export const loadTripadvisorByExistingProposalHotel = async (hotel: any, lang: string) => {
  const bpid = hotel.codes.find((code: any) => code.name === 'BPID')?.code;
  if (!bpid) {
    return undefined;
  }

  return hotelsTripadvisor({
    bpid,
    lang,
  });
};

export const loadGoogleByExistingProposalHotel = async (
  hotel: any,
  lang: string,
  token: string,
  currency?: string,
) => {
  const bpid = hotel.codes.find((code: any) => code.name === 'BPID')?.code;
  const checkIn = hotel.rooms?.[0]?.startDate || '';
  const checkOut = hotel.rooms?.[0]?.endtDate || '';
  const latitude = Number(hotel.geoLocation?.latitude);
  const longitude = Number(hotel.geoLocation?.longitude);

  if (!bpid || !checkIn || !checkOut || Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return undefined;
  }

  const pax = hotel.rooms?.[0]?.pax;
  const childrenOccupancy = resolveChildrenOccupancy(pax?.children, pax?.childrenAges);

  return searchSerpHotels({
    recaptchaToken: token,
    cleanHotelName: hotel.hotel?.name || '',
    bpid,
    countryCode: hotel.country?.name || '',
    lat: latitude,
    lon: longitude,
    cleanAddress: hotel.adress || '',
    query: {
      q: `Hotel: ${hotel.hotel?.name || ''}. Address: ${hotel.adress || ''}, ${hotel.city?.name || ''}, ${hotel.country?.name || ''}`,
      gL: 'il',
      hl: lang,
      checkIn,
      checkOut,
      adults: pax?.adults || 0,
      ...childrenOccupancy,
      currency: currency?.toUpperCase() || 'USD',
    },
  });
};

export const enrichExistingProposalHotelsData = async (
  existingProposal: ProposalData,
  lang: string,
  recaptchaToken?: string,
  onUpdated?: (proposal: ProposalData) => void,
) => {
  const enrichedProposal: ProposalData = JSON.parse(JSON.stringify(existingProposal));
  const hotelRecords: Array<{
    productIndex: number;
    itemIndex: number;
    hotelIndex: number;
    currency: string;
  }> = [];

  enrichedProposal.listOfProducts?.forEach((product, productIndex) => {
    product.listOfItems?.forEach((item, itemIndex) => {
      if (item.proposal?.code !== 'HTL' || !item.hotels?.length) {
        return;
      }

      item.hotels.forEach((_, hotelIndex) => {
        hotelRecords.push({
          productIndex,
          itemIndex,
          hotelIndex,
          currency: product.currency,
        });
      });
    });
  });

  if (!hotelRecords.length) {
    return enrichedProposal;
  }

  hotelRecords.forEach(({ productIndex, itemIndex, hotelIndex }) => {
    const hotel: any =
      enrichedProposal.listOfProducts[productIndex].listOfItems[itemIndex].hotels[hotelIndex];
    hotel.serpHotelEntireRequested = !!recaptchaToken;
    hotel.serpHotelEntireLoading = !!recaptchaToken;
    hotel.tripAdvisorHotelEntireRequested = true;
    hotel.tripAdvisorHotelEntireLoading = true;
  });

  void Promise.all(
    hotelRecords.map(async ({ productIndex, itemIndex, hotelIndex, currency }) => {
      const hotel: any =
        enrichedProposal.listOfProducts[productIndex].listOfItems[itemIndex].hotels[hotelIndex];

      const googlePromise = !recaptchaToken
        ? Promise.resolve(undefined)
        : loadGoogleByExistingProposalHotel(hotel, lang, recaptchaToken, currency)
            .then((googleData) => {
              if (googleData?.serpResults?.serpHotel) {
                hotel.serpHotelEntire = googleData.serpResults.serpHotel;
              }
            })
            .finally(() => {
              hotel.serpHotelEntireLoading = false;
              onUpdated?.(JSON.parse(JSON.stringify(enrichedProposal)));
            });

      const tripadvisorPromise = loadTripadvisorByExistingProposalHotel(hotel, lang)
        .then((tripAdvisorData) => {
          if (tripAdvisorData?.tripAdvisorHotel) {
            hotel.tripAdvisorHotelEntire = tripAdvisorData.tripAdvisorHotel;
          }
        })
        .finally(() => {
          hotel.tripAdvisorHotelEntireLoading = false;
          onUpdated?.(JSON.parse(JSON.stringify(enrichedProposal)));
        });

      await Promise.all([googlePromise, tripadvisorPromise]);
    }),
  );

  return enrichedProposal;
};

interface UploadFileForProposalParams {
  base64File: string;
  fileName: string;
}
export const uploadFileForProposal = async (
  params: UploadFileForProposalParams,
): Promise<any | undefined> => {
  try {
    const url = 'Utils/UploadFileForProposal';
    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export interface ProposalGalleryResponse {
  hasErrors: boolean;
  errorMessage: string;
  userGallery: string[];
  companyGallery: Record<string, string[]>;
}

export const getProposalGallery = async (): Promise<ProposalGalleryResponse | undefined> => {
  try {
    const cached = sessionStorage.getItem('proposalGallery');
    if (cached) {
      return JSON.parse(cached);
    }

    const data = (await axios.get('Utils/GetProposalGallery')).data as ProposalGalleryResponse;
    if (data && !data.hasErrors) {
      sessionStorage.setItem('proposalGallery', JSON.stringify(data));
    }
    return data;
  } catch {
    return undefined;
  }
};
